import { useLocation } from "wouter";
import { Logo } from "@/components/Logo";
import QuoteCarousel from "@/components/QuoteCarousel";

const SAGE = "#A8B5A2";

const SECTION_STYLE: React.CSSProperties = {
  marginBottom: "40px",
};

const H2_STYLE: React.CSSProperties = {
  fontSize: "18px",
  fontWeight: 400,
  letterSpacing: "-0.01em",
  marginBottom: "12px",
  color: "#0a0a0a",
};

const P_STYLE: React.CSSProperties = {
  fontSize: "15px",
  fontWeight: 300,
  lineHeight: 1.75,
  color: "#3a3a3a",
  marginBottom: "12px",
};

const UL_STYLE: React.CSSProperties = {
  paddingLeft: "20px",
  marginBottom: "12px",
};

const LI_STYLE: React.CSSProperties = {
  fontSize: "15px",
  fontWeight: 300,
  lineHeight: 1.75,
  color: "#3a3a3a",
  marginBottom: "6px",
};

export default function TermsOfService() {
  const [, navigate] = useLocation();

  return (
    <div style={{ minHeight: "100vh", background: "#fafafa", fontFamily: "'Inter', sans-serif" }}>
      {/* Navbar */}
      <nav style={{
        borderBottom: "1px solid rgba(0,0,0,0.06)",
        background: "rgba(252,252,252,0.95)",
        backdropFilter: "blur(16px)",
        position: "sticky",
        top: 0,
        zIndex: 50,
      }}>
        <div style={{ maxWidth: "1152px", margin: "0 auto", padding: "0 20px", height: "64px", display: "flex", alignItems: "center" }}>
          <button
            onClick={() => navigate("/")}
            style={{ display: "flex", alignItems: "center", gap: "10px", background: "none", border: "none", cursor: "pointer", padding: 0 }}
          >
            <Logo iconSize={30} />
          </button>
        </div>
      </nav>

      {/* Content */}
      <div style={{ maxWidth: "760px", margin: "0 auto", padding: "64px 20px 80px" }}>
        <p style={{ fontSize: "11px", fontWeight: 400, letterSpacing: "0.1em", textTransform: "uppercase", color: SAGE, marginBottom: "12px" }}>
          Legal
        </p>
        <h1 style={{ fontSize: "clamp(28px, 4vw, 42px)", fontWeight: 200, letterSpacing: "-0.03em", marginBottom: "8px" }}>
          Terms of Service
        </h1>
        <p style={{ fontSize: "14px", fontWeight: 300, color: "#888", marginBottom: "48px" }}>
          Last updated: April 26, 2026
        </p>

        <div style={SECTION_STYLE}>
          <p style={P_STYLE}>
            These Terms of Service ("Terms") govern your use of the DebtBridge Foundation website and services ("DebtBridge" or "Service"), operated by DebtBridge Foundation, a 501(c)(3) pending nonprofit organization ("we," "us," or "our"). By accessing or using DebtBridge, you agree to be bound by these Terms.
          </p>
          <p style={P_STYLE}>
            <strong>If you do not agree to these Terms, do not use DebtBridge.</strong>
          </p>
        </div>

        <div style={SECTION_STYLE}>
          <h2 style={H2_STYLE}>1. Service Description</h2>
          <p style={P_STYLE}>
            DebtBridge is a community-funded debt relief platform. Donors contribute funds into thematic pools. When a pool reaches its funding threshold, DebtBridge uses the pooled funds to purchase and eliminate qualifying consumer debts on behalf of enrolled debtors. DebtBridge issues debt forgiveness letters and tax receipts as appropriate.
          </p>
          <p style={P_STYLE}>
            DebtBridge is a nonprofit charitable service, not a financial institution, creditor, debt collector, or credit reporting agency.
          </p>
        </div>

        <div style={SECTION_STYLE}>
          <h2 style={H2_STYLE}>2. Eligibility</h2>
          <p style={P_STYLE}>You must be at least 18 years old to use DebtBridge. By creating an account, you represent that:</p>
          <ul style={UL_STYLE}>
            <li style={LI_STYLE}>You are at least 18 years of age.</li>
            <li style={LI_STYLE}>You have the legal capacity to enter into these Terms.</li>
            <li style={LI_STYLE}>You are a resident of the United States (or a jurisdiction where DebtBridge's services are lawfully available).</li>
            <li style={LI_STYLE}>Your use of DebtBridge does not violate any applicable law or regulation.</li>
          </ul>
        </div>

        <div style={SECTION_STYLE}>
          <h2 style={H2_STYLE}>3. Donor Terms</h2>
          <p style={P_STYLE}>
            <strong>Donation Finality:</strong> All donations are final and non-refundable once submitted to a pool. Donations are voluntary charitable contributions and do not guarantee any specific outcome or beneficiary.
          </p>
          <p style={P_STYLE}>
            <strong>Tax Deductibility:</strong> DebtBridge is a 501(c)(3) pending nonprofit organization. Donations may be tax-deductible to the extent permitted by law. We issue donation receipts in compliance with IRS requirements. Consult your tax advisor for guidance on deductibility.
          </p>
          <p style={P_STYLE}>
            <strong>501(c)(3) Status:</strong> Our application for 501(c)(3) tax-exempt status is pending with the IRS. Until formal determination is received, tax deductibility is not guaranteed. We will notify users of any change in our tax-exempt status.
          </p>
          <p style={P_STYLE}>
            <strong>Pool Allocation:</strong> Donations to a pool are used exclusively to purchase debts within that pool's theme. DebtBridge may retain a small operational fee (disclosed at the time of donation) to cover platform costs.
          </p>
          <p style={P_STYLE}>
            <strong>Recurring Donations:</strong> Monthly recurring donations will be charged on approximately the same day each month. You may cancel recurring donations at any time from your donor dashboard.
          </p>
        </div>

        <div style={SECTION_STYLE}>
          <h2 style={H2_STYLE}>4. Debtor Terms</h2>
          <p style={P_STYLE}>
            <strong>Eligibility:</strong> To receive debt relief, you must submit a valid debt document and meet DebtBridge's eligibility criteria (determined at DebtBridge's sole discretion). Submission does not guarantee relief.
          </p>
          <p style={P_STYLE}>
            <strong>FCRA Consent:</strong> By submitting a debt document, you affirmatively consent to DebtBridge obtaining, accessing, and using your consumer financial information (including information contained in the submitted documents) for the purpose of evaluating and facilitating debt relief on your behalf, as permitted under the Fair Credit Reporting Act.
          </p>
          <p style={P_STYLE}>
            <strong>Debt Purchase:</strong> When a pool matures, DebtBridge may purchase your debt from the original creditor or a debt buyer. By enrolling, you consent to DebtBridge acting as a debt buyer for purposes of settling your debt at or below face value.
          </p>
          <p style={P_STYLE}>
            <strong>Tax Consequences:</strong> Debt forgiveness may constitute taxable income to you under the Internal Revenue Code. DebtBridge may issue a Form 1099-C for cancelled debts of $600 or more. Consult your tax advisor.
          </p>
          <p style={P_STYLE}>
            <strong>No Guarantee:</strong> Submission to a relief pool does not guarantee your debt will be selected or cleared. Queue position and selection are determined by DebtBridge's prioritization algorithm.
          </p>
        </div>

        <div style={SECTION_STYLE}>
          <h2 style={H2_STYLE}>5. Prohibited Conduct</h2>
          <p style={P_STYLE}>You agree not to:</p>
          <ul style={UL_STYLE}>
            <li style={LI_STYLE}>Submit false, fraudulent, or fabricated debt documents.</li>
            <li style={LI_STYLE}>Use DebtBridge to launder money or facilitate illegal activity.</li>
            <li style={LI_STYLE}>Attempt to manipulate pool funding or queue priority through fraudulent means.</li>
            <li style={LI_STYLE}>Create multiple accounts to circumvent eligibility limits.</li>
            <li style={LI_STYLE}>Reverse engineer, scrape, or interfere with the DebtBridge platform.</li>
            <li style={LI_STYLE}>Impersonate another person or entity.</li>
          </ul>
          <p style={P_STYLE}>Violations may result in immediate account termination and may be reported to law enforcement.</p>
        </div>

        <div style={SECTION_STYLE}>
          <h2 style={H2_STYLE}>6. Intellectual Property</h2>
          <p style={P_STYLE}>
            All content, software, and materials on DebtBridge (excluding user-uploaded debt documents) are owned by or licensed to DebtBridge Foundation. You may not reproduce, distribute, or create derivative works without written permission.
          </p>
        </div>

        <div style={SECTION_STYLE}>
          <h2 style={H2_STYLE}>7. Disclaimer of Warranties</h2>
          <p style={P_STYLE}>
            DebtBridge is provided "as is" and "as available" without warranties of any kind, express or implied. We do not warrant that the Service will be uninterrupted, error-free, or achieve any particular debt relief outcome. DebtBridge is not a licensed financial advisor, attorney, or debt counselor. Nothing on this platform constitutes legal, financial, or tax advice.
          </p>
        </div>

        <div style={SECTION_STYLE}>
          <h2 style={H2_STYLE}>8. Limitation of Liability</h2>
          <p style={P_STYLE}>
            To the fullest extent permitted by law, DebtBridge Foundation and its officers, directors, volunteers, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of the Service, including without limitation: loss of data, failed debt relief, tax consequences of debt forgiveness, or unauthorized access to your account.
          </p>
          <p style={P_STYLE}>
            Our total liability to you for any claim arising from use of the Service shall not exceed the greater of $100 or the amount you donated in the 12 months preceding the claim.
          </p>
        </div>

        <div style={SECTION_STYLE}>
          <h2 style={H2_STYLE}>9. Dispute Resolution</h2>
          <p style={P_STYLE}>
            Any dispute arising from these Terms or your use of DebtBridge shall first be addressed through good-faith negotiation. If unresolved, disputes shall be submitted to binding arbitration under the American Arbitration Association Commercial Arbitration Rules, conducted in English in the State of Delaware. You waive the right to participate in class action litigation or class-wide arbitration.
          </p>
        </div>

        <div style={SECTION_STYLE}>
          <h2 style={H2_STYLE}>10. Governing Law</h2>
          <p style={P_STYLE}>
            These Terms are governed by the laws of the State of Delaware, without regard to conflict of law principles, except where superseded by applicable federal law.
          </p>
        </div>

        <div style={SECTION_STYLE}>
          <h2 style={H2_STYLE}>11. State Solicitation Notice</h2>
          <p style={P_STYLE}>
            DebtBridge may not be registered to solicit charitable donations in all states. Solicitation of donations may not be permitted in states where DebtBridge has not met applicable registration requirements. Residents of states requiring registration are encouraged to verify our status with their state's charity registration office before donating.
          </p>
        </div>

        <div style={SECTION_STYLE}>
          <h2 style={H2_STYLE}>12. Changes to Terms</h2>
          <p style={P_STYLE}>
            We reserve the right to modify these Terms at any time. Material changes will be communicated by email and by updating the "Last updated" date. Your continued use of DebtBridge after changes constitutes acceptance of the revised Terms.
          </p>
        </div>

        <div style={SECTION_STYLE}>
          <h2 style={H2_STYLE}>13. Contact</h2>
          <p style={P_STYLE}>
            Questions about these Terms:<br />
            <strong>DebtBridge Foundation</strong><br />
            Email: <a href="mailto:legal@debtbridge.org" style={{ color: SAGE }}>legal@debtbridge.org</a><br />
            EIN: pending
          </p>
        </div>
      </div>

      <QuoteCarousel />

      {/* Footer */}
      <footer style={{ borderTop: "1px solid rgba(0,0,0,0.06)", padding: "28px 20px", textAlign: "center" }}>
        <p style={{ fontSize: "12px", fontWeight: 300, color: "#999" }}>
          © 2026 DebtBridge Foundation · EIN: pending ·{" "}
          <a href="/privacy" style={{ color: "#999", textDecoration: "underline" }}>Privacy</a>
          {" · "}
          <a href="/terms" style={{ color: "#999", textDecoration: "underline" }}>Terms</a>
        </p>
      </footer>
    </div>
  );
}
